import React, { useState, Suspense, lazy } from 'react';
import { Activity, CheckCircle2, AlertTriangle, XCircle, Info, ChevronDown, ChevronRight, Lightbulb, Shield, Zap, Database, Globe, Server, Clock, HelpCircle, BookOpen, Star, Users, ShoppingBag, Megaphone, BarChart3, Settings, MessageSquare, FileText, Bell, DollarSign, Video, Repeat } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface AdminSystemHealthProps {
  success: (msg: string) => void;
  setActiveTab: (tab: string) => void;
}
// Section registry — describes every admin section for health checks and hints
const ADMIN_SECTIONS = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: Activity,
  description: 'Главная панель с KPI и графиками',
  hint: 'Здесь отображаются ключевые метрики платформы. Используйте переключатель периодов в верхней панели для анализа трендов.'
}, {
  id: 'users',
  label: 'Пользователи',
  icon: Users,
  description: 'Управление пользователями, блокировки, risk-флаги',
  hint: 'Кликните на пользователя для просмотра профиля. Используйте Risk-флаги для пометки подозрительных аккаунтов.'
}, {
  id: 'astrologers',
  label: 'Астрологи',
  icon: Star,
  description: 'Профили астрологов, рейтинги, модерация',
  hint: 'Следите за Risk-индикаторами — система автоматически помечает подозрительное поведение (увод клиентов, обмен контактами).'
}, {
  id: 'verification',
  label: 'Верификация',
  icon: Shield,
  description: 'Очередь проверки документов астрологов',
  hint: 'Проверяйте документы в порядке очереди. Каждая заявка содержит сертификаты, портфолио и видео-презентацию.'
}, {
  id: 'orders',
  label: 'Заказы',
  icon: ShoppingBag,
  description: 'Все заказы, статусы, споры',
  hint: 'Раскройте строку заказа для просмотра деталей. Обращайте внимание на заказы со статусом "Спор" — они требуют ручного решения.'
}, {
  id: 'consultations',
  label: 'Консультации',
  icon: Video,
  description: 'Видео/аудио/чат консультации',
  hint: 'Система AI автоматически анализирует чаты на нарушения. Красные флаги означают попытку увода клиента с платформы.'
}, {
  id: 'ai_reports',
  label: 'AI / ИИ-отчёты',
  icon: Zap,
  description: 'Генерация отчётов, шаблоны, песочница',
  hint: 'Используйте Песочницу (Sandbox) для тестирования шаблонов без создания реального заказа. Лимит: 5 генераций в минуту.'
}, {
  id: 'finance',
  label: 'Финансы',
  icon: DollarSign,
  description: 'Платежи, выплаты астрологам, возвраты',
  hint: 'Три вкладки: Платежи (входящие), Выплаты (астрологам), Возвраты. Следите за Chargeback — это критичные случаи.'
}, {
  id: 'subscriptions',
  label: 'Подписки',
  icon: Repeat,
  description: 'Управление подписками пользователей',
  hint: 'Здесь можно отменить подписку пользователя или изменить её параметры. Отмена вступает в силу в конце текущего периода.'
}, {
  id: 'advertising',
  label: 'Реклама и размещения',
  icon: Megaphone,
  description: 'Рекламные слоты, баннеры, email-кампании',
  hint: 'Управляйте рекламными слотами на сайте. Каждый слот имеет свою цену и приоритет. Используйте очередь для планирования.'
}, {
  id: 'content',
  label: 'Контент / CMS',
  icon: FileText,
  description: 'Статьи блога, страницы, SEO',
  hint: 'Создавайте и редактируйте контент. Все изменения сохраняются как черновик до публикации.'
}, {
  id: 'notifications',
  label: 'Уведомления',
  icon: Bell,
  description: 'Центр уведомлений, фильтры, правила',
  hint: 'Фильтруйте по категориям: Система, Финансы, Пользователи, Безопасность. Непрочитанные помечены синей точкой.'
}, {
  id: 'full_chat',
  label: 'Чат с пользователями',
  icon: MessageSquare,
  description: 'Прямое общение и рассылки',
  hint: 'Две вкладки: Чаты (личные диалоги) и Рассылки (массовые сообщения). Используйте быстрые шаблоны для типовых ответов.'
}, {
  id: 'analytics',
  label: 'Аналитика',
  icon: BarChart3,
  description: 'Детальная аналитика, UTM, воронки',
  hint: 'Подключите Google Analytics и Meta Pixel в настройках для отслеживания конверсий. Дашборд обновляется в реальном времени.'
}, {
  id: 'settings',
  label: 'Настройки',
  icon: Settings,
  description: 'Платёжные системы, уведомления, трекинг',
  hint: 'Критичные настройки: платёжные системы (Stripe, YooKassa), email-уведомления, коды отслеживания (GA, Meta Pixel).'
}];
// Simulated system status
const SYSTEM_STATUS = {
  api: {
    status: 'ok' as const,
    label: 'API сервер',
    latency: '45ms'
  },
  database: {
    status: 'ok' as const,
    label: 'База данных',
    latency: '12ms'
  },
  ai: {
    status: 'ok' as const,
    label: 'AI генерация',
    latency: '2.1s'
  },
  payments: {
    status: 'ok' as const,
    label: 'Платежи',
    latency: '180ms'
  },
  email: {
    status: 'warning' as const,
    label: 'Email сервис',
    latency: '850ms'
  },
  cdn: {
    status: 'ok' as const,
    label: 'CDN / Медиа',
    latency: '35ms'
  }
};
export function AdminSystemHealth({
  success,
  setActiveTab
}: AdminSystemHealthProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [showAllHints, setShowAllHints] = useState(false);
  const [activeHealthTab, setActiveHealthTab] = useState<'status' | 'guide' | 'architecture'>('status');
  const statusIcon = (status: 'ok' | 'warning' | 'error') => {
    switch (status) {
      case 'ok':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-7011" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-7012" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-rose-500" data-id="element-7013" />;
    }
  };
  const statusColor = (status: 'ok' | 'warning' | 'error') => {
    switch (status) {
      case 'ok':
        return 'bg-emerald-50 border-emerald-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'error':
        return 'bg-rose-50 border-rose-200';
    }
  };
  return <div className="space-y-6" data-id="element-7014">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-7015">
        <div data-id="element-7016">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-7017">
            <Activity className="w-5 h-5 text-indigo-600" data-id="element-7018" />
            Здоровье системы
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-7019">
            Мониторинг, справочник разделов и подсказки для администраторов
          </p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit" data-id="element-7020">
        {[{
        id: 'status' as const,
        label: 'Статус системы',
        icon: <Server className="w-3.5 h-3.5" data-id="element-7021" />
      }, {
        id: 'guide' as const,
        label: 'Справочник разделов',
        icon: <BookOpen className="w-3.5 h-3.5" data-id="element-7022" />
      }, {
        id: 'architecture' as const,
        label: 'Архитектура',
        icon: <Database className="w-3.5 h-3.5" data-id="element-7023" />
      }].map(tab => <button key={tab.id} onClick={() => setActiveHealthTab(tab.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${activeHealthTab === tab.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-7024">
            {tab.icon}
            {tab.label}
          </button>)}
      </div>

      {/* === TAB: System Status === */}
      {activeHealthTab === 'status' && <div className="space-y-4" data-id="element-7025">
          {/* Overall status */}
          <Card padding="md" className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-white" data-id="element-7026">
            <div className="flex items-center gap-3" data-id="element-7027">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center" data-id="element-7028">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-7029" />
              </div>
              <div data-id="element-7030">
                <div className="font-bold text-emerald-900" data-id="element-7031">
                  Все системы работают
                </div>
                <div className="text-xs text-emerald-600 mt-0.5" data-id="element-7032">
                  Последняя проверка: только что • Uptime: 99.97%
                </div>
              </div>
            </div>
          </Card>

          {/* Service grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-7033">
            {Object.entries(SYSTEM_STATUS).map(([key, service]) => <div key={key} className={`p-4 rounded-xl border ${statusColor(service.status)} transition-colors`} data-id="element-7034">
                <div className="flex items-center justify-between mb-2" data-id="element-7035">
                  {statusIcon(service.status)}
                  <span className="text-[10px] font-mono text-slate-400" data-id="element-7036">
                    {service.latency}
                  </span>
                </div>
                <div className="text-sm font-medium text-slate-900" data-id="element-7037">
                  {service.label}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-7038">
                  {service.status === 'ok' ? 'Работает нормально' : service.status === 'warning' ? 'Повышенная задержка' : 'Недоступен'}
                </div>
              </div>)}
          </div>

          {/* Error isolation info */}
          <Card padding="md" className="border-indigo-200 bg-indigo-50/50" data-id="element-7039">
            <div className="flex items-start gap-3" data-id="element-7040">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-7041">
                <Shield className="w-4 h-4 text-indigo-600" data-id="element-7042" />
              </div>
              <div data-id="element-7043">
                <div className="text-sm font-bold text-indigo-900" data-id="element-7044">
                  Защита от каскадных сбоев
                </div>
                <p className="text-xs text-indigo-700 mt-1 leading-relaxed" data-id="element-7045">
                  Каждый раздел админки изолирован. Если один раздел упадёт
                  из-за ошибки, остальные продолжат работать. Вы увидите
                  сообщение об ошибке только в проблемном разделе с возможностью
                  перезагрузить его или скопировать ошибку для разработчика.
                </p>
              </div>
            </div>
          </Card>
        </div>}

      {/* === TAB: Section Guide === */}
      {activeHealthTab === 'guide' && <div className="space-y-4" data-id="element-7046">
          <Card padding="sm" className="bg-amber-50 border-amber-200" data-id="element-7047">
            <div className="flex items-center gap-2 px-2 py-1" data-id="element-7048">
              <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-7049" />
              <p className="text-xs text-amber-800" data-id="element-7050">
                <strong data-id="element-7051">Совет:</strong> Кликните на любой раздел, чтобы увидеть
                подсказку. Нажмите «Перейти» чтобы открыть раздел.
              </p>
            </div>
          </Card>

          <div className="space-y-2" data-id="element-7052">
            {ADMIN_SECTIONS.map(section => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;
          return <div key={section.id} className={`rounded-xl border transition-all ${isExpanded ? 'border-indigo-200 bg-indigo-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-7053">
                  <button onClick={() => setExpandedSection(isExpanded ? null : section.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-id="element-7054">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isExpanded ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-7055">
                      <Icon className="w-4 h-4" data-id="element-7056" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-7057">
                      <div className="text-sm font-medium text-slate-900" data-id="element-7058">
                        {section.label}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate" data-id="element-7059">
                        {section.description}
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-7060" />
                  </button>

                  {isExpanded && <div className="px-4 pb-4 pt-1" data-id="element-7061">
                      <div className="bg-white rounded-lg border border-indigo-100 p-3" data-id="element-7062">
                        <div className="flex items-start gap-2" data-id="element-7063">
                          <HelpCircle className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" data-id="element-7064" />
                          <p className="text-xs text-slate-700 leading-relaxed" data-id="element-7065">
                            {section.hint}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setActiveTab(section.id)} className="mt-3 flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors" data-id="element-7066">
                        Перейти в раздел
                        <ChevronRight className="w-3.5 h-3.5" data-id="element-7067" />
                      </button>
                    </div>}
                </div>;
        })}
          </div>
        </div>}

      {/* === TAB: Architecture === */}
      {activeHealthTab === 'architecture' && <div className="space-y-4" data-id="element-7068">
          <Card padding="md" className="border-slate-200" data-id="element-7069">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-7070">
              <Database className="w-4 h-4 text-indigo-500" data-id="element-7071" />
              Архитектура админ-панели
            </h3>
            <div className="space-y-3 text-xs text-slate-600 leading-relaxed" data-id="element-7072">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-7073">
                <div className="font-bold text-slate-900 mb-1" data-id="element-7074">
                  📁 Структура файлов
                </div>
                <pre className="font-mono text-[11px] text-slate-500 whitespace-pre leading-5" data-id="element-7075">
                  {`components/
├── admin/
│   ├── AdminSectionBoundary.tsx  ← Изоляция ошибок
│   ├── AdminSystemHealth.tsx     ← Эта страница
│   ├── AdminDashboard.tsx        ← Главный дашборд
│   ├── AdminUsersManager.tsx     ← Пользователи
│   ├── AdminAstrologersManager.tsx
│   ├── AdminFinanceFullManager.tsx
│   ├── AdminAdvertisingFullManager.tsx
│   ├── AdminOrdersManager.tsx
│   ├── AdminConsultationsManager.tsx
│   ├── AdminAIReportsManager.tsx
│   └── ... (ещё 10+ модулей)
├── ui/          ← Переиспользуемые компоненты
├── layout/      ← Header, Footer, Layout
└── ErrorBoundary.tsx  ← Глобальная защита`}
                </pre>
              </div>

              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-7076">
                <div className="font-bold text-emerald-900 mb-1" data-id="element-7077">
                  ✅ Что уже хорошо
                </div>
                <ul className="space-y-1 text-emerald-800" data-id="element-7078">
                  <li data-id="element-7079">
                    • Каждый раздел — отдельный файл (AdminUsersManager,
                    AdminFinanceFullManager и т.д.)
                  </li>
                  <li data-id="element-7080">
                    • UI-компоненты вынесены в components/ui/ (Button, Card,
                    Badge, Tabs...)
                  </li>
                  <li data-id="element-7081">
                    • Контексты отделены (UserContext, CartContext, BlogContext)
                  </li>
                  <li data-id="element-7082">• Данные в отдельной папке data/</li>
                  <li data-id="element-7083">• ErrorBoundary для глобальных ошибок</li>
                </ul>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-7084">
                <div className="font-bold text-amber-900 mb-1" data-id="element-7085">
                  ⚡ Что можно улучшить в будущем
                </div>
                <ul className="space-y-1.5 text-amber-800" data-id="element-7086">
                  <li data-id="element-7087">
                    <strong data-id="element-7088">1. Lazy Loading страниц</strong> — загружать каждую
                    страницу только когда она нужна (lazy + Suspense). Ускорит
                    первую загрузку.
                  </li>
                  <li data-id="element-7089">
                    <strong data-id="element-7090">2. Вынести стейт AdminPage</strong> — создать
                    useAdminState() хук, чтобы AdminPage.tsx стал тонкой
                    оболочкой (сейчас 3400+ строк).
                  </li>
                  <li data-id="element-7091">
                    <strong data-id="element-7092">3. React Router</strong> — заменить hash-роутинг на
                    полноценный роутер с вложенными маршрутами для админки.
                  </li>
                  <li data-id="element-7093">
                    <strong data-id="element-7094">4. Серверный стейт</strong> — React Query / SWR для
                    кэширования данных вместо useState в AdminPage.
                  </li>
                  <li data-id="element-7095">
                    <strong data-id="element-7096">5. Тесты</strong> — unit-тесты для критичных
                    компонентов (финансы, платежи, модерация).
                  </li>
                </ul>
              </div>

              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100" data-id="element-7097">
                <div className="font-bold text-indigo-900 mb-1" data-id="element-7098">
                  🛡️ Изоляция ошибок (уже работает)
                </div>
                <p className="text-indigo-800" data-id="element-7099">
                  Каждый раздел обёрнут в AdminSectionBoundary. Если компонент
                  упадёт, вы увидите сообщение только в этом разделе. Остальная
                  админка продолжит работать. Можно перезагрузить упавший раздел
                  или скопировать ошибку.
                </p>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}